import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Code, Globe, Users, Menu, X, Target, Telescope, Briefcase, BookOpen, GraduationCap, ArrowUp } from 'lucide-react';
import { toast } from 'sonner';
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";

import msmeLogo from "src/images/MSME_LOGO-1.png";
import mngLogo from "src/images/DHAYANITHI ALAGESAN.png";
import logo from "src/images/logo.png";
import heroBg from "src/images/hero.jpg";
import course_pos_img from "src/images/courses_poster.png"
import codesprint from "src/images/codesprint.png"
import fullstack from "src/images/fullstack.jpeg"

const newLaunchPosters = [
  {
    id: 1,
    img: course_pos_img,
    link: "https://forms.gle/1itqR6Cn42RJJ5h8A"
  },
  {
    id: 2,
    img: codesprint,
    link: "https://forms.gle/5EX8AucMvH48t9417"
  },
  {
    id: 3,
    img: fullstack,
    link: "https://forms.gle/nFxk1rHWPC8Fw4LB6"
  }
];


const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showFloating, setShowFloating] = useState(false);
  const [loadingLaunches, setLoadingLaunches] = useState(true);
  const newLaunchRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setLoadingLaunches(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const el = newLaunchRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("nudge-once");
          observer.disconnect(); // run only once
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, []);


  // Show floating enroll button only when hero is scrolled out of view
  useEffect(() => {
    const hero = document.getElementById('home');
    if (!hero) {
      setShowFloating(true);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // when hero is intersecting, hide floating; when out of view, show floating
        setShowFloating(!entry.isIntersecting);
      });
    }, { threshold: 0.01 });

    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // replace your existing handleSubmit with this
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // build FormData from controlled state
      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('email', formData.email);
      fd.append('phone', formData.phone);
      fd.append('message', formData.message);

      const res = await fetch('https://formspree.io/f/meoyqzor', {
        method: 'POST',
        body: fd,
        headers: {
          Accept: 'application/json' // request JSON response so Formspree doesn't redirect
        }
      });

      if (res.ok) {
        toast.success('Message sent successfully! We will get back to you soon.');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        // read error details if available
        const data = await res.json().catch(() => ({}));
        console.error('Formspree error response:', data);
        // show a helpful message
        if (data && data.error) {
          toast.error(data.error);
        } else {
          toast.error('Failed to send message. Please try again.');
        }
      }
    } catch (err) {
      console.error('Contact form network error:', err);
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div>
      {/* Header */}
      <header className={`header ${scrolled ? 'scrolled' : ''}`}>
        <nav className="nav-container">
          <Link to="/" className="logo" data-testid="logo-link">
            <img src={logo} alt="PK Placement Services Logo" className="logo-img" />
            <span>PK Placement Services</span>
          </Link>

          <ul className="nav-links" data-testid="nav-links">
            <li><a href="#home" onClick={() => scrollToSection('home')} data-testid="nav-home">Home</a></li>
            <li><a href="#services" onClick={() => scrollToSection('services')} data-testid="nav-services">Services</a></li>
            <li><a href="#new-launch" onClick={() => scrollToSection('courses')} data-testid="nav-courses">Courses</a></li>
            <li><a href="/team" data-testid="nav-about">About</a></li>
            <li><a href="#contact" onClick={() => scrollToSection('contact')} data-testid="nav-contact">Contact</a></li>
          </ul>
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
          {mobileMenuOpen && (
            <div className="mobile-nav active" data-testid="mobile-nav">
              <ul>
                <li><a href="#home" onClick={() => scrollToSection('home')}>Home</a></li>
                <li><a href="#services" onClick={() => scrollToSection('services')}>Services</a></li>
                <li><a href="#new-launch" onClick={() => scrollToSection('courses')}>Courses</a></li>
                <li><a href="/team">About</a></li>
                <li><a href="#contact" onClick={() => scrollToSection('contact')}>Contact</a></li>
              </ul>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section className="hero" id="home" data-testid="hero-section">
        <img
          src={heroBg}
          alt="Background"
          className="hero-bg"
        />
        <div className="hero-content">
          <h1 data-testid="hero-title">PK Placement Services</h1>
          <p data-testid="hero-subtitle">Learning Today, Leading Tomorrow...</p>
          <div className="hero-buttons">
            {/* <a href="https://docs.google.com/forms/d/e/1FAIpQLSeYQRWbaaxAJdhYytRFQijQVMCFacSpP6wi6F_aJredV0htmQ/viewform?usp=dialog" className="btn-primary" data-testid="enroll-now-btn" target="_blank">
              Enroll Now
            </a> */}
            <a href="#courses" className="btn-secondary" onClick={() => scrollToSection('courses')} data-testid="explore-courses-btn">
              Explore Courses
            </a>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="section" id="mission" data-testid="mission-section">
        <h2 className="section-title" data-testid="mission-title">Our Mission & Vision</h2>
        <div className="mission-grid">
          <div className="mission-card" data-testid="mission-card-mission">
            <div className="mission-content">
              <div className="service-icon">
                <Target size={36} color="#2563eb" />
              </div>
              <h3>Our Mission</h3>
              <p>Our mission at PK Placement Services Ltd is to deliver high quality training and internship opportunities, provide hands-on learning across emerging technologies and support students in building strong technical and professional competencies.</p>
            </div>
          </div>
          <div className="mission-card" data-testid="mission-card-vision">
            <div className="mission-content">
              <div className="service-icon">
                <Telescope size={36} color="#2563eb" />
              </div>
              <h3>Our Vision</h3>
              <p>PK Placement Services Ltd aims to empower students with industry-ready skills and practical experience, creating a future where every learner can confidently enter the professional world.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section" id="services" data-testid="services-section">
        <h2 className="section-title" data-testid="services-title">Our Services</h2>
        <div className="services-grid">
          <div className="service-card" data-testid="service-card-app-dev">
            <div className="service-icon">
              <Code size={36} color="#2563eb" />
            </div>
            <h3>App Development</h3>
            <p>Build cutting-edge mobile applications with modern frameworks and best practices for iOS and Android platforms.</p>
          </div>
          <div className="service-card" data-testid="service-card-web-dev">
            <div className="service-icon">
              <Globe size={36} color="#2563eb" />
            </div>
            <h3>Website Development</h3>
            <p>Create responsive and scalable web applications using the latest technologies and industry standards.</p>
          </div>
          <div className="service-card" data-testid="service-card-placement">
            <div className="service-icon">
              <Users size={36} color="#2563eb" />
            </div>
            <h3>Placement Readiness Support</h3>
            <p>Comprehensive training and guidance to prepare you for successful career placements in top companies.</p>
          </div>
        </div>

        <div className="services-grid">
          <div className="service-card" data-testid="service-card-client-services">
            <div className="service-icon">
              <Briefcase size={36} color="#2563eb" />
            </div>
            <h3>Client Services</h3>
            <p>
              End-to-end client support including requirement analysis, project planning,
              execution, and post-delivery assistance to ensure complete satisfaction.
            </p>
          </div>

          <div className="service-card" data-testid="service-card-courses">
            <div className="service-icon">
              <BookOpen size={36} color="#2563eb" />
            </div>
            <h3>Courses</h3>
            <p>
              Industry-focused courses designed to build strong technical foundations
              through hands-on learning, real-world projects, and expert guidance.
            </p>
          </div>

          <div className="service-card" data-testid="service-card-internships">
            <div className="service-icon">
              <GraduationCap size={36} color="#2563eb" />
            </div>
            <h3>Internships</h3>
            <p>
              Practical internship programs that provide real project experience,
              mentorship, and exposure to professional work environments.
            </p>
          </div>
        </div>

      </section>

      <section className="section" id="new-launch">
        <h2 className="section-title">🚀 New Launch</h2>
        <div ref={newLaunchRef} className="new-launch-carousel">
          <Swiper
            spaceBetween={16}
            slidesPerView={1}
            // centeredSlides={true}
            breakpoints={{
              640: { slidesPerView: 1.2 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 }
            }}
          >
            {newLaunchPosters.map(item => (
              <SwiperSlide key={item.id}>
                <div className="launch-card">
                  <div className="poster-wrapper">
                    <img
                      src={item.img}
                      alt="New Launch Poster"
                      className="launch-poster"
                    />

                    {/* Hover overlay button (desktop only) */}
                    <a href={item.link} className="launch-btn-overlay" target='_blank'>

                    </a>
                  </div>

                  {/* Mobile / tablet button */}
                  <a href={item.link} className="launch-btn-mobile" target='_blank'>
                    Register Now
                  </a>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

      {/* Courses Preview Section */}
      <section className="section" id="courses" data-testid="courses-section">
        <h2 className="section-title" data-testid="courses-title">Our Courses</h2>
        <div className="courses-preview-grid">
          <div className="course-card" data-testid="course-card-fullstack">
            <div className="course-content">
              <h3>Full Stack Web Development</h3>
              <p>Master both frontend and backend technologies including React, Node.js, MongoDB, and modern deployment practices.</p>
            </div>
          </div>
          <div className="course-card" data-testid="course-card-devops">
            <div className="course-content">
              <h3>Dev Ops Engineer</h3>
              <p>Learn automation, CI/CD pipelines, containerization, and cloud deployment essential for DevOps roles.</p>
            </div>
          </div>
          <div className="course-card" data-testid="course-card-genai">
            <div className="course-content">
              <h3>Gen AI</h3>
              <p>Learn Generative AI workflows including ML, NLP, LLM APIs, prompt engineering, and RAG-based fine-tuning.</p>
            </div>
          </div>
        </div>
        <div className="view-all-container">
          <a href="/courses" className="btn-primary" data-testid="view-all-courses-btn">View All Courses</a>
        </div>
      </section>

      {/* About Section */}
      {/* <section className="section" id="about" data-testid="about-section">
        <h2 className="section-title" data-testid="about-title">About Our Managing Director</h2>
        <div className="about-content">
          <img
            src={mngLogo}
            alt="Managing Director"
            className="md-photo"
            data-testid="md-photo"
          />
          <div className="md-info">
            <h3 data-testid="md-name">Dhayanithi Alagesan</h3>
            <p data-testid="md-description">
              Associate Proffessor in Sri Sairam Engineering College, With over 15 years of experience in IT training and placement services, leading PK Placement Services with a vision to bridge the gap between academic learning and industry requirements. His dedication to student success and industry partnerships has helped 10,000+ students to be successful in their careers in technology.
            </p>
            <a href="/team" className="btn-primary" data-testid="know-more-team-btn">Know More About Our Team</a>
          </div>
        </div>
      </section> */}

      {/* Map Section */}
      <section className="section map-section" id="location" data-testid="map-section">
        <h2 className="section-title" data-testid="map-title">Visit Our Office</h2>
        <div className="map-container">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3889.22908503697!2d80.06435499999999!3d12.892985800000002!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a52f566eb435095%3A0x8362f7bd3823b50f!2s65%2C%205th%20Cross%20St%2C%20near%20Brown%20color%20building%2C%20Ram%20Nagar%2C%20Mannivakkam%2C%20Tamil%20Nadu%20600048!5e0!3m2!1sen!2sin!4v1764561595613!5m2!1sen!2sin"
            loading="lazy"
            title="Office Location"
            data-testid="map-iframe"
          ></iframe>
        </div>
        <div className="map-address">
          <p data-testid="office-address">65, 5th street, Ram Nagar Manivakkam, Chennai , Tamil Nadu -600048</p>
          <a href="https://maps.app.goo.gl/5xW4FbeaEoP2uoMXA" target="_blank" rel="noopener noreferrer" data-testid="get-directions-link">
            Get Directions →
          </a>
        </div>
      </section>

      {/* Accreditation Section */}
      <section className="section" id="accreditation" data-testid="accreditation-section">
        <h2 className="section-title" data-testid="accreditation-title">Accredited By</h2>
        <div className="accreditation-logo">
          <img
            src={msmeLogo}
            alt="MSME"
            data-testid="msme-logo"
          />
        </div>
      </section>

      {/* Contact Section */}
      <section className="section" id="contact" data-testid="contact-section">
        <h2 className="section-title" data-testid="contact-title">Contact Us</h2>
        <form className="contact-form" data-testid="contact-form" onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              data-testid="contact-name-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              data-testid="contact-email-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="phone">Phone</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              data-testid="contact-phone-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              required
              data-testid="contact-message-input"
            ></textarea>
          </div>
          <button type="submit" className="btn-submit" disabled={submitting} data-testid="contact-submit-btn">
            {submitting ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Quick Links</h4>
            <div className="flex">
              <ul className="footer-links flex flex-col items-start">
                <li><a href="#home" onClick={() => scrollToSection('home')}>Home</a></li>
                <li><a href="#services" onClick={() => scrollToSection('services')}>Services</a></li>
                <li><a href="/courses">Courses</a></li>
                {/* <li><a href="/terms-and-conditions">Terms & Conditions</a></li> */}
              </ul>

              <ul className="footer-links px-10 flex flex-col items-start">
                <li><a href="/team">About</a></li>
                <li><a href="#contact" onClick={() => scrollToSection('contact')}>Contact</a></li>
              </ul>
            </div>
          </div>

          <div className="footer-section">
            <h4>Connect With Us</h4>
            <div className="social-links flex flex-col">
              {/* WhatsApp */}
              <div className="flex items-center">
                <a
                  href="https://wa.me/+918056175066"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="social-whatsapp"
                >
                  <i className="fab fa-whatsapp"></i>
                </a>
                <span className='px-2 cursor-pointer' onClick={() => window.open("https://wa.me/+918056175066", "_blank")}>+91 80561 75066</span>
              </div>
              {/* Email */}
              <div className="flex items-center">
                <a
                  href="mailto:forpkqueries@gmail.com"
                  rel="noopener noreferrer"
                  data-testid="social-email"
                >
                  <i className="fas fa-envelope"></i>
                </a>
                <span className='px-2 cursor-pointer' onClick={() => window.open("mailto:forpkqueries@gmail.com", "_blank")}>forpkqueries@gmail.com</span>
              </div>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; 2025 PK Placement Services. All rights reserved.</p>
        </div>
      </footer>

      {/* Floating Button - Home: only show when scrolled below hero */}
      {showFloating && (
        <button
          className="floating-btt-btn"
          data-testid="floating-btt-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Back to Top"
        >
          <ArrowUp className="btt-icon" />
        </button>
      )}

    </div>
  );
};

export default Home;